/**
 * @cfleet/api-client - Customer Invoice Hooks
 *
 * React Query hooks for customer invoice management operations.
 * These endpoints require customer authentication.
 *
 * Invoices represent billing documents for completed deliveries.
 * Operations include listing, viewing details, and downloading PDFs.
 *
 * @example
 * ```tsx
 * import {
 *   useCustomerInvoices,
 *   useCustomerInvoice,
 *   useDownloadInvoicePDF,
 * } from '@cfleet/api-client';
 *
 * // List invoices with pagination and filters
 * const { data } = useCustomerInvoices({ page: 1, per_page: 20, status: 'Open' }, { authConfig });
 *
 * // Get single invoice details
 * const { data } = useCustomerInvoice('INV-2025-00000456', { authConfig });
 *
 * // Download invoice PDF
 * const { mutate: downloadPDF } = useDownloadInvoicePDF({ authConfig });
 * downloadPDF('INV-2025-00000456');
 * ```
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import createClient from 'openapi-fetch';
import type { CustomerInvoicingPaths, CustomerInvoicingComponents } from '../generated';
import { createAuthMiddleware, type AuthMiddlewareConfig } from '../middleware';

// ─────────────────────────────────────────────────────────────────────────────
// Type Aliases from Generated Types
// ─────────────────────────────────────────────────────────────────────────────

type CustomerInvoice = CustomerInvoicingComponents['schemas']['CustomerInvoice'];
type InvoiceListResponse = CustomerInvoicingComponents['schemas']['InvoiceListResponse'];
type InvoiceStatus = CustomerInvoicingComponents['schemas']['InvoiceStatus'];
type InvoiceSummary = CustomerInvoicingComponents['schemas']['InvoiceSummary'];
type ErrorResponse = CustomerInvoicingComponents['schemas']['ErrorResponse'];

// Re-export types for consumers
export type { CustomerInvoice, InvoiceListResponse, InvoiceStatus, InvoiceSummary };

// ─────────────────────────────────────────────────────────────────────────────
// Client Factory
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Creates a client specifically for customer invoicing operations.
 * This avoids type conflicts from combining multiple path types.
 */
function createCustomerInvoicingClient(authConfig: AuthMiddlewareConfig) {
  const client = createClient<CustomerInvoicingPaths>({
    baseUrl: authConfig.baseUrl,
  });
  client.use(createAuthMiddleware(authConfig));
  return client;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook Configuration Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Configuration required for all customer invoice hooks.
 * The authConfig provides authentication and base URL settings.
 */
export interface UseCustomerInvoiceHooksConfig {
  authConfig: AuthMiddlewareConfig;
}

/**
 * Parameters for listing customer invoices.
 */
export interface ListCustomerInvoicesParams {
  /** Page number (1-based) */
  page?: number;
  /** Items per page */
  per_page?: number;
  /** Filter by invoice status */
  status?: InvoiceStatus;
  /** Filter invoices issued on or after this date (YYYY-MM-DD) */
  from?: string;
  /** Filter invoices issued on or before this date (YYYY-MM-DD) */
  to?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Query Key Factory
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Query key factory for customer invoice queries.
 * Provides consistent query key generation for caching and invalidation.
 */
export const customerInvoiceQueryKeys = {
  all: ['customer-invoices'] as const,
  lists: () => [...customerInvoiceQueryKeys.all, 'list'] as const,
  list: (params: ListCustomerInvoicesParams) =>
    [...customerInvoiceQueryKeys.lists(), params] as const,
  details: () => [...customerInvoiceQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...customerInvoiceQueryKeys.details(), id] as const,
};

// ─────────────────────────────────────────────────────────────────────────────
// Hooks
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to fetch a paginated list of customer invoices.
 *
 * Returns invoices for the authenticated customer with optional
 * filtering by status (Open/Paid/Overdue) and date range.
 *
 * The response includes a summary with total outstanding amount
 * and count of overdue invoices.
 *
 * @example
 * ```tsx
 * const { data, isLoading, isError, refetch } = useCustomerInvoices(
 *   { page: 1, per_page: 20, status: 'Open' },
 *   { authConfig }
 * );
 *
 * // Access invoices
 * const invoices = data?.data ?? [];
 *
 * // Access pagination info
 * const { total_items, total_pages, has_next } = data?.pagination ?? {};
 *
 * // Access summary
 * const { total_outstanding, overdue_count } = data?.summary ?? {};
 * ```
 */
export function useCustomerInvoices(
  params: ListCustomerInvoicesParams = {},
  config: UseCustomerInvoiceHooksConfig
) {
  return useQuery({
    queryKey: customerInvoiceQueryKeys.list(params),
    queryFn: async (): Promise<InvoiceListResponse> => {
      const client = createCustomerInvoicingClient(config.authConfig);

      const { data, error } = await client.GET('/customer/invoices', {
        params: { query: params },
      });

      if (error) {
        const apiError = error as ErrorResponse;
        throw new Error(apiError.detail || 'Failed to fetch invoices');
      }

      return data;
    },
    enabled: Boolean(config.authConfig),
  });
}

/**
 * Hook to fetch a single invoice by ID.
 *
 * Returns detailed invoice information including the list of
 * order IDs included in the invoice.
 *
 * @example
 * ```tsx
 * const { data: invoice, isLoading, isError } = useCustomerInvoice(
 *   'INV-2025-00000456',
 *   { authConfig }
 * );
 *
 * // Access invoice details
 * console.log(invoice?.amount, invoice?.status, invoice?.orders);
 * ```
 */
export function useCustomerInvoice(
  invoiceId: string,
  config: UseCustomerInvoiceHooksConfig
) {
  return useQuery({
    queryKey: customerInvoiceQueryKeys.detail(invoiceId),
    queryFn: async (): Promise<CustomerInvoice> => {
      const client = createCustomerInvoicingClient(config.authConfig);

      const { data, error } = await client.GET('/customer/invoices/{invoiceId}', {
        params: { path: { invoiceId } },
      });

      if (error) {
        const apiError = error as ErrorResponse;
        throw new Error(apiError.detail || 'Failed to fetch invoice');
      }

      return data;
    },
    enabled: Boolean(invoiceId) && Boolean(config.authConfig),
  });
}

/**
 * Hook to download an invoice as a PDF document.
 *
 * This mutation fetches the PDF as a blob and triggers a browser download.
 * The filename follows the pattern: Invoice-{invoiceId}.pdf
 *
 * @example
 * ```tsx
 * const downloadPDF = useDownloadInvoicePDF({ authConfig });
 *
 * // Trigger download
 * <button
 *   onClick={() => downloadPDF.mutate('INV-2025-00000456')}
 *   disabled={downloadPDF.isPending}
 * >
 *   {downloadPDF.isPending ? 'Downloading...' : 'Download PDF'}
 * </button>
 *
 * // Handle errors
 * if (downloadPDF.isError) {
 *   console.error('Download failed:', downloadPDF.error);
 * }
 * ```
 */
export function useDownloadInvoicePDF(config: UseCustomerInvoiceHooksConfig) {
  return useMutation({
    mutationFn: async (invoiceId: string): Promise<void> => {
      const client = createCustomerInvoicingClient(config.authConfig);

      const { data, error } = await client.GET('/customer/invoices/{invoiceId}/download', {
        params: { path: { invoiceId } },
        parseAs: 'blob',
      });

      if (error) {
        const apiError = error as ErrorResponse;
        throw new Error(apiError.detail || 'Failed to download invoice');
      }

      // Create blob URL and trigger download
      const blob = data as Blob;
      const url = URL.createObjectURL(blob);

      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `Invoice-${invoiceId}.pdf`;
      anchor.click();

      // Clean up
      URL.revokeObjectURL(url);
    },
  });
}
